import React, { useState } from 'react';
import { DYNAMIC_VEHICLE_FRAMEWORKS } from './constants';

const StudentProgressReport = ({ student }) => {
    const [expandedCategory, setExpandedCategory] = useState(null);

    if (!student) return null;

    const categoryFramework = DYNAMIC_VEHICLE_FRAMEWORKS[student.training_category || "Car (Manual)"] || {};
    const studentSkills = student.skills || {};

    const cardColors = ['#0066cc', '#2f855a', '#d69e2e', '#805ad5', '#319795', '#e53e3e'];

    // Process categories: Mastered (5), Competent (3-4), Support Needed (1-2)
    const categoryEntries = Object.entries(categoryFramework).map(([category, skills]) => {
        let mastered = 0; // score 5 -> Green
        let competent = 0; // score 3-4 -> Amber
        let developing = 0; // score 1-2 -> Red

        skills.forEach(skill => {
            const score = studentSkills[skill] || 1;
            if (score === 5) mastered++;
            else if (score >= 3) competent++;
            else developing++;
        });

        const totalSkills = skills.length;
        const totalPossible = totalSkills * 5;
        const earned = skills.reduce((sum, skill) => sum + (studentSkills[skill] || 1), 0);
        const percentage = Math.round((earned / totalPossible) * 100) || 0;

        return { 
            category, 
            skills, 
            percentage, 
            mastered, 
            competent, 
            developing, 
            totalSkills 
        };
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* --- INTERACTIVE CATEGORY CARDS WITH TRAFFIC LIGHT DONUT CHARTS --- */}
            <div style={{ background: '#f8f9fa', padding: '20px', borderRadius: '12px', border: '1px solid #cbd5e0' }}>
                <h4 style={{ margin: '0 0 5px 0', color: '#0066cc' }}>📊 Core Syllabus Category Breakdown</h4>
                <p style={{ fontSize: '11px', color: '#64748b', marginBottom: '15px' }}>Click any category card to drill down into traffic-light sub-skill meters.</p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '15px' }}>
                    {categoryEntries.map((item, idx) => {
                        const themeColor = cardColors[idx % cardColors.length];
                        const isExpanded = expandedCategory === item.category;

                        const radius = 35;
                        const circumference = 2 * Math.PI * radius;
                        
                        const masteredPct = (item.mastered / item.totalSkills) * 100;
                        const competentPct = (item.competent / item.totalSkills) * 100;
                        const developingPct = (item.developing / item.totalSkills) * 100;

                        const masteredOffset = 0;
                        const competentOffset = -((masteredPct / 100) * circumference);
                        const developingOffset = -(((masteredPct + competentPct) / 100) * circumference);

                        return (
                            <div 
                                key={item.category} 
                                onClick={() => setExpandedCategory(isExpanded ? null : item.category)}
                                style={{ 
                                    background: '#ffffff', 
                                    padding: '15px', 
                                    borderRadius: '10px', 
                                    border: isExpanded ? `2px solid ${themeColor}` : '1px solid #e2e8f0', 
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.03)' 
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#111111' }}>
                                        {item.category} {isExpanded ? '▼' : '▶'}
                                    </span>
                                    <span style={{ fontSize: '11px', fontWeight: 'bold', padding: '2px 8px', borderRadius: '4px', background: `${themeColor}15`, color: themeColor }}>
                                        {item.percentage}% Overall
                                    </span>
                                </div>

                                {/* Card Main Row: Traffic Light Mini Donut Chart + Quick Stats */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', background: '#f8f9fa', padding: '10px', borderRadius: '8px', border: '1px solid #edf2f7' }}>
                                    <svg width="80" height="80" viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
                                        <circle r={radius} cx="50" cy="50" fill="transparent" stroke="#e2e8f0" strokeWidth="20" />
                                        {/* Mastered Slice (Green) */}
                                        {item.mastered > 0 && (
                                            <circle r={radius} cx="50" cy="50" fill="transparent" stroke="#38a169" strokeWidth="20"
                                                strokeDasharray={`${(masteredPct / 100) * circumference} ${circumference}`}
                                                strokeDashoffset={masteredOffset} />
                                        )}
                                        {/* Competent Slice (Amber) */}
                                        {item.competent > 0 && (
                                            <circle r={radius} cx="50" cy="50" fill="transparent" stroke="#d69e2e" strokeWidth="20"
                                                strokeDasharray={`${(competentPct / 100) * circumference} ${circumference}`}
                                                strokeDashoffset={competentOffset} />
                                        )}
                                        {/* Developing/Support Slice (Red) */}
                                        {item.developing > 0 && (
                                            <circle r={radius} cx="50" cy="50" fill="transparent" stroke="#e53e3e" strokeWidth="20"
                                                strokeDasharray={`${(developingPct / 100) * circumference} ${circumference}`}
                                                strokeDashoffset={developingOffset} />
                                        )}
                                    </svg>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#38a169' }}></div>
                                            <span><b>{item.mastered}</b> Mastered (5)</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#d69e2e' }}></div>
                                            <span><b>{item.competent}</b> Competent (3-4)</span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#e53e3e' }}></div>
                                            <span><b>{item.developing}</b> Support Needed (1-2)</span>
                                        </div>
                                    </div>
                                </div>

                                
{/* --- EXPANDED TRAFFIC-LIGHT SUB-SKILL DRILL-DOWN --- */}
                                {isExpanded && (
                                    <div style={{ marginTop: '12px', borderTop: '2px solid #e2e8f0', paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }} onClick={e => e.stopPropagation()}>
                                        <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sub-Skills Breakdown:</span>
                                        {item.skills.map(skill => {
                                            const score = studentSkills[skill] || 1;
                                            
                                            // Traffic light styling (Green for 5, Amber for 3-4, Red for 1-2)
                                            const bgTint = score === 5 ? '#f0fff4' : score >= 3 ? '#fffaf0' : '#fff5f5';
                                            const borderColor = score === 5 ? '#9ae6b4' : score >= 3 ? '#fbd38d' : '#feb2b2';
                                            const textColor = score === 5 ? '#22543d' : score >= 3 ? '#9c4221' : '#9b2c2c';
                                            const badgeBg = score === 5 ? '#c6f6d5' : score >= 3 ? '#feebc8' : '#fed7d7';
                                            const label = score === 5 ? 'Mastered' : score >= 3 ? 'Competent' : 'Support Needed';

                                            return (
                                                <div key={skill} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: bgTint, padding: '8px 12px', borderRadius: '6px', fontSize: '11px', border: `1px solid ${borderColor}` }}>
                                                    <span style={{ color: '#1a202c', fontWeight: '600' }}>{skill}</span>
                                                    <span style={{ fontWeight: 'bold', color: textColor, background: badgeBg, padding: '3px 8px', borderRadius: '4px', border: `1px solid ${borderColor}` }}>
                                                        {label} ({score}/5)
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

        </div>
    );
};

export default StudentProgressReport;